/**
 * Player settings, persisted to localStorage.
 *
 * Preferences only — never a token, never anything private. Reads are defensive
 * because localStorage can hold whatever a previous version wrote, or whatever
 * somebody typed into a console.
 */

export interface PlayerSettings {
  quality: 'auto' | 'low' | 'medium' | 'high';
  /** Stored as an integer 20–300; divided by 20000 to get radians per pixel. */
  sensitivity: number;
  invertY: boolean;
  showDiagnostics: boolean;
  reducedMotion: boolean;
  /** Percentage, 90–150. */
  textScale: number;
}

const KEY = 'emberreach.settings.v1';

export const DEFAULT_SETTINGS: PlayerSettings = {
  quality: 'auto',
  sensitivity: 110,
  invertY: false,
  showDiagnostics: false,
  reducedMotion: false,
  textScale: 100
};

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function loadSettings(): PlayerSettings {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    // Private browsing can make localStorage throw on access. Defaults are fine.
    return { ...DEFAULT_SETTINGS };
  }
  if (raw === null) return { ...DEFAULT_SETTINGS };

  try {
    const parsed = JSON.parse(raw) as Partial<PlayerSettings>;
    const quality = parsed.quality;
    return {
      quality:
        quality === 'low' || quality === 'medium' || quality === 'high' || quality === 'auto'
          ? quality
          : DEFAULT_SETTINGS.quality,
      sensitivity: clamp(parsed.sensitivity, 20, 300, DEFAULT_SETTINGS.sensitivity),
      invertY: bool(parsed.invertY, DEFAULT_SETTINGS.invertY),
      showDiagnostics: bool(parsed.showDiagnostics, DEFAULT_SETTINGS.showDiagnostics),
      reducedMotion: bool(parsed.reducedMotion, DEFAULT_SETTINGS.reducedMotion),
      textScale: clamp(parsed.textScale, 90, 150, DEFAULT_SETTINGS.textScale)
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: PlayerSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Full or blocked storage is not worth interrupting play over.
  }
}

/** Radians of camera rotation per pixel of pointer movement. */
export function sensitivityToRadians(sensitivity: number): number {
  return sensitivity / 20000;
}

export function applyAccessibility(settings: PlayerSettings): void {
  document.documentElement.style.setProperty('--text-scale', String(settings.textScale / 100));
  document.body.classList.toggle('reduced-motion', settings.reducedMotion);
}
